/**
 * Local storage service for tracking created gardens
 * Used for client-side duplicate name detection in Phase 1
 *
 * Phase 2: This will be replaced with server-side per-user tracking
 * when user authentication is implemented
 */

const STORAGE_KEY = 'gartenplaner_my_gardens';

export const gardenStorage = {
  /**
   * Get all gardens from localStorage
   * @returns {Array<{id: string, name: string, createdAt: string}>}
   */
  getAll: () => {
    try {
      const data = localStorage.getItem(STORAGE_KEY);
      return data ? JSON.parse(data) : [];
    } catch (error) {
      console.error('Error reading gardens from localStorage:', error);
      return [];
    }
  },

  /**
   * Add a new garden to the list
   * @param {{id: string, name: string}} garden
   */
  add: (garden) => {
    const gardens = gardenStorage.getAll();
    gardens.push({
      id: garden.id,
      name: garden.name,
      createdAt: new Date().toISOString(),
    });
    localStorage.setItem(STORAGE_KEY, JSON.stringify(gardens));
  },

  /**
   * Update garden name
   * @param {string} gardenId - Garden UUID
   * @param {string} newName - New garden name
   */
  update: (gardenId, newName) => {
    const gardens = gardenStorage.getAll();
    const index = gardens.findIndex(g => g.id === gardenId);
    if (index !== -1) {
      gardens[index].name = newName;
      localStorage.setItem(STORAGE_KEY, JSON.stringify(gardens));
    }
  },

  /**
   * Remove garden from list
   * @param {string} gardenId - Garden UUID
   */
  remove: (gardenId) => {
    const gardens = gardenStorage.getAll();
    const filtered = gardens.filter(g => g.id !== gardenId);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filtered));
  },

  /**
   * Find garden by name (case-insensitive with whitespace trimming)
   * @param {string} name - Garden name to search for
   * @returns {{id: string, name: string, createdAt: string} | undefined}
   */
  findByName: (name) => {
    const gardens = gardenStorage.getAll();
    return gardens.find(g =>
      g.name.toLowerCase().trim() === name.toLowerCase().trim()
    );
  },

  /**
   * Clear all gardens (used for testing or reset)
   */
  clear: () => {
    localStorage.removeItem(STORAGE_KEY);
  },
};
